import moment from 'moment';
import { memo, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Col, Form, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import NoteDropdown from '../noteDropdown';
import { useClickAway } from 'react-use';
import {
  getDragDropStyle,
  isHiddenInPrint,
  itemBottomGapStyle,
  itemSettingStyle,
  itemTitleStyle,
  lineHeightStyle,
} from '../../../../utils/helpers';

const DynamicPrescriptionItem = ({
  element,
  prescriptionItems,
  setIsModalOpen,
  deleteItem,
  selectedItems = [],
  setSelectedItems,
  isHistoryPage = false,
}) => {
  console.log('selectedItems: ==============================', selectedItems);
  const allNotes = useSelector((state) => state?.prescriptionReducer?.prescriptionInfo?.itemElementNotes || []);
  const [isEdit, setIsEdit] = useState('');
  const itemName = element.name;

  const reorder = (list, startIndex, endIndex) => {
    const arr = Array.from(list);
    const [removed] = arr.splice(startIndex, 1);
    arr.splice(endIndex, 0, removed);
    return arr;
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    setSelectedItems(
      reorder(selectedItems, result.source.index, result.destination.index),
    );
  };

  const handleFieldChange = (index, fieldName, val) => {
    const updated = [...selectedItems];
    updated[index] = { ...updated[index], [fieldName]: val };
    setSelectedItems(updated);
  };

  const ref = useRef(null);
  useClickAway(ref, () => setIsEdit(''));

  const displayTitle = element?.alterName?.length
    ? element.alterName
    : itemName.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const editMode = (item, index) => (
    <Row ref={ref} className="selected-row-edit" key={item.name}>
      <Col className="name pl-0 pr-0" lg="4" md="4" sm="12" xs="12">
        <Form.Control
          type="text"
          size="sm"
          defaultValue={item.name}
          onBlur={(e) => handleFieldChange(index, 'name', e.target.value)}
        />
      </Col>
      <Col lg="3" md="3" sm="12" xs="12" className="responsive">
        <Form.Control
          type="date"
          size="sm"
          defaultValue={item.date}
          onChange={(e) => handleFieldChange(index, 'date', e.target.value)}
        />
      </Col>
      <Col lg="4" md="4" sm="12" xs="12" className="responsive pl-0 pr-0">
        <NoteDropdown
          value={item?.note || ''}
          onChange={(val) => handleFieldChange(index, 'note', val)}
          placeholder="Add Note"
          suggestions={allNotes.filter(
            (n) => n.elementType === itemName && n.itemElement?.toLowerCase() === item?.name?.toLowerCase(),
          )}
        />
      </Col>
      <Col md="1" className="text-right pr-0">
        <i
          className="far fa-times-circle pt-2"
          aria-hidden="true"
          onClick={() => setIsEdit('')}
        />
      </Col>
    </Row>
  );

  const prescriptionItemCSSObject = () => itemBottomGapStyle(element, selectedItems.length === 0);

  return (
    <div
      className={`row item-row ${selectedItems?.length ? 'data-item' : ''}`}
      key={itemName}
      style={prescriptionItemCSSObject()}
    >
      <span
        className={`prescription-item ${
          isHiddenInPrint(prescriptionItems, itemName) ? 'item-title-hide' : ''
        }`}
        style={itemTitleStyle(
          prescriptionItems?.items?.[itemName]?.titleStyle || {},
        )}
        {...(!isHistoryPage
          ? {
              onClick: (e) => {
                e.stopPropagation();
                setIsModalOpen(true);
              },
            }
          : {})}
      >
        <span>{displayTitle}</span>
      </span>
      {selectedItems?.length > 0 ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={`droppable-dynamic-${itemName}`}>
            {(provided) => (
              <ul
                className="sub-child-list drag-drop"
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={lineHeightStyle(itemName, prescriptionItems)}
              >
                {selectedItems?.map((item, index) => (
                  <Draggable
                    key={`${item.name}-${index}`}
                    draggableId={`dynamic-${itemName}-${index}`}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <li
                        key={index}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...getDragDropStyle(
                            snapshot.isDragging,
                            provided.draggableProps.style,
                          ),
                          position: 'relative',
                        }}
                      >
                        <div
                          onClick={() => setIsEdit(index)}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        >
                          <span
                            style={itemSettingStyle(
                              prescriptionItems?.items?.[itemName]?.itemStyle ||
                                {},
                            )}
                          >
                            {item.name}{' '}
                            {item.date !== ''
                              ? '- ' + moment(item.date).format('DD-MM-YYYY')
                              : ''}
                            {prescriptionItems?.items?.[itemName]?.notePosition === 'same-line' && item.note
                              ? ` - ${item.note}`
                              : ''}
                          </span>
                          {!isHistoryPage && (
                            <i
                              className="far fa-times-circle"
                              style={{ cursor: 'pointer', flexShrink: 0 }}
                              aria-hidden="true"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteItem(item.name);
                              }}
                            />
                          )}
                        </div>
                        {item.note && prescriptionItems?.items?.[itemName]?.notePosition !== 'same-line' && (
                          <div
                            style={itemSettingStyle(
                              prescriptionItems?.items?.[itemName]
                                ?.subItemStyle || {},
                            )}
                            className="pl-2 font-italic"
                          >
                            - {item.note}
                          </div>
                        )}
                        {index === isEdit && editMode(item, index)}
                      </li>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        ''
      )}
    </div>
  );
};

export default memo(DynamicPrescriptionItem);
